import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  Loader2,
  Zap,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Play,
  Square,
} from 'lucide-react';
import type { CRMAccount } from './CRMAccountForm';

interface BulkBackfillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

interface AccountPreview {
  account: CRMAccount;
  affected_count: number;
  total_linked: number;
}

interface AccountResult {
  account_id: string;
  account_name: string;
  success: boolean;
  project: number;
  cost_code: number;
  reference: number;
  error?: string;
}

type Phase = 'idle' | 'loading-preview' | 'preview' | 'running' | 'complete';

const BulkBackfillDialog: React.FC<BulkBackfillDialogProps> = ({
  open,
  onOpenChange,
  onComplete,
}) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [eligibleAccounts, setEligibleAccounts] = useState<AccountPreview[]>([]);
  const [totalEstimatedJobs, setTotalEstimatedJobs] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentAccountName, setCurrentAccountName] = useState('');
  const [results, setResults] = useState<AccountResult[]>([]);
  const cancelledRef = useRef(false);

  const loadPreview = useCallback(async () => {
    setPhase('loading-preview');
    setEligibleAccounts([]);
    setTotalEstimatedJobs(0);
    setResults([]);
    cancelledRef.current = false;

    try {
      // Fetch all active accounts that have at least one default configured
      const { data: accounts, error } = await supabase
        .from('crm_accounts')
        .select('*')
        .eq('is_active', true)
        .order('account_name', { ascending: true });

      if (error) throw error;

      // Filter to accounts with at least one default
      const withDefaults = (accounts || []).filter((a: CRMAccount) => {
        return (
          (a.default_project && a.default_project.trim() !== '') ||
          (a.default_cost_code && a.default_cost_code.trim() !== '') ||
          (a.default_reference && a.default_reference.trim() !== '')
        );
      });

      if (withDefaults.length === 0) {
        setEligibleAccounts([]);
        setPhase('preview');
        return;
      }

      // For each eligible account, get preview count
      const previews: AccountPreview[] = [];
      let totalJobs = 0;

      for (const account of withDefaults) {
        try {
          const { data, error: previewErr } = await supabase.functions.invoke('crm-backfill', {
            body: {
              account_id: account.account_id,
              default_project: account.default_project,
              default_cost_code: account.default_cost_code,
              default_reference: account.default_reference,
              preview_only: true,
            },
          });

          if (previewErr) {
            console.error(`Preview error for ${account.account_id}:`, previewErr);
            continue;
          }

          if (data?.error) {
            console.error(`Preview error for ${account.account_id}:`, data.error);
            continue;
          }

          const affectedCount = data?.affected_count || 0;
          const totalLinked = data?.total_linked || 0;

          if (affectedCount > 0) {
            previews.push({
              account,
              affected_count: affectedCount,
              total_linked: totalLinked,
            });
            totalJobs += affectedCount;
          }
        } catch (err) {
          console.error(`Preview error for ${account.account_id}:`, err);
        }
      }

      setEligibleAccounts(previews);
      setTotalEstimatedJobs(totalJobs);
      setPhase('preview');
    } catch (err: any) {
      console.error('Bulk preview error:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to load bulk preview',
        variant: 'destructive',
      });
      setPhase('idle');
    }
  }, []);

  const handleExecute = useCallback(async () => {
    if (eligibleAccounts.length === 0) return;

    setPhase('running');
    setCurrentIndex(0);
    setResults([]);
    cancelledRef.current = false;

    const allResults: AccountResult[] = [];

    for (let i = 0; i < eligibleAccounts.length; i++) {
      if (cancelledRef.current) break;

      const { account } = eligibleAccounts[i];
      setCurrentIndex(i);
      setCurrentAccountName(account.account_name || account.account_id);

      try {
        const { data, error } = await supabase.functions.invoke('crm-backfill', {
          body: {
            account_id: account.account_id,
            default_project: account.default_project,
            default_cost_code: account.default_cost_code,
            default_reference: account.default_reference,
            preview_only: false,
          },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        const u = data?.updated || {};
        allResults.push({
          account_id: account.account_id,
          account_name: account.account_name || '',
          success: true,
          project: u.project || 0,
          cost_code: u.cost_code || 0,
          reference: u.reference || 0,
        });
      } catch (err: any) {
        allResults.push({
          account_id: account.account_id,
          account_name: account.account_name || '',
          success: false,
          project: 0,
          cost_code: 0,
          reference: 0,
          error: err.message || 'Unknown error',
        });
      }

      setResults([...allResults]);
    }

    setPhase('complete');
    onComplete();
  }, [eligibleAccounts, onComplete]);

  const handleCancel = () => {
    cancelledRef.current = true;
  };

  const handleClose = () => {
    if (phase === 'running') return; // Don't close while running
    setPhase('idle');
    setEligibleAccounts([]);
    setResults([]);
    onOpenChange(false);
  };

  // When dialog opens, load preview
  React.useEffect(() => {
    if (open && phase === 'idle') {
      loadPreview();
    }
    if (!open) {
      setPhase('idle');
    }
  }, [open, phase, loadPreview]);

  // Summary calculations
  const totalProjectUpdated = results.reduce((sum, r) => sum + r.project, 0);
  const totalCostCodeUpdated = results.reduce((sum, r) => sum + r.cost_code, 0);
  const totalReferenceUpdated = results.reduce((sum, r) => sum + r.reference, 0);
  const totalFieldsUpdated = totalProjectUpdated + totalCostCodeUpdated + totalReferenceUpdated;
  const successCount = results.filter((r) => r.success).length;
  const failCount = results.filter((r) => !r.success).length;

  const progressPercent =
    phase === 'running' && eligibleAccounts.length > 0
      ? Math.round(((currentIndex + 1) / eligibleAccounts.length) * 100)
      : phase === 'complete'
      ? 100
      : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle className="golden-text text-xl flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Bulk Backfill All Accounts
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Run the backfill operation across all active CRM accounts with defaults configured.
          </DialogDescription>
        </DialogHeader>

        {/* Loading Preview Phase */}
        {phase === 'loading-preview' && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin mb-3 text-yellow-400" />
            <p className="text-sm font-medium">Scanning accounts and estimating affected jobs...</p>
            <p className="text-xs mt-1">This may take a moment</p>
          </div>
        )}

        {/* Preview Phase */}
        {phase === 'preview' && (
          <div className="space-y-4">
            {eligibleAccounts.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-400" />
                <p className="text-foreground font-medium">No accounts need backfilling</p>
                <p className="text-muted-foreground text-sm mt-1">
                  All active accounts with defaults configured already have their TaxiCaller fields populated.
                </p>
              </div>
            ) : (
              <>
                {/* Summary stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-secondary/50 rounded-lg p-4 border border-border">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Accounts to Process</p>
                    <p className="text-2xl font-bold text-foreground mt-1">{eligibleAccounts.length}</p>
                  </div>
                  <div className="bg-yellow-900/20 rounded-lg p-4 border border-yellow-700/40">
                    <p className="text-xs text-yellow-400 uppercase tracking-wide">Estimated Affected Jobs</p>
                    <p className="text-2xl font-bold text-yellow-300 mt-1">{totalEstimatedJobs}</p>
                  </div>
                </div>

                {/* Account list */}
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="max-h-[280px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/60 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs text-muted-foreground font-medium">Account</th>
                          <th className="text-right px-3 py-2 text-xs text-muted-foreground font-medium">Linked Jobs</th>
                          <th className="text-right px-3 py-2 text-xs text-muted-foreground font-medium">Affected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleAccounts.map((ep) => (
                          <tr key={ep.account.id} className="border-t border-border/50">
                            <td className="px-3 py-2">
                              <span className="text-foreground font-medium">{ep.account.account_name}</span>
                              <span className="text-muted-foreground ml-2 text-xs">({ep.account.account_id})</span>
                            </td>
                            <td className="px-3 py-2 text-right text-muted-foreground">{ep.total_linked}</td>
                            <td className="px-3 py-2 text-right">
                              <Badge className="bg-yellow-900/40 text-yellow-400 border-yellow-700/50 text-xs">
                                {ep.affected_count}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex items-start gap-2 bg-yellow-900/20 border border-yellow-700/40 rounded-lg p-3">
                  <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-yellow-300/80">
                    <p className="font-medium text-yellow-300">This will update TaxiCaller job records</p>
                    <p className="mt-0.5">
                      Only blank Project, Cost Code, and Reference fields will be filled from CRM defaults.
                      Existing non-blank values will not be overwritten.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Running Phase */}
        {phase === 'running' && (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Processing account {currentIndex + 1} of {eligibleAccounts.length}
                </span>
                <span className="text-foreground font-mono text-xs">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-3" />
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" />
                Backfilling: <span className="text-foreground font-medium">{currentAccountName}</span>
              </p>
            </div>

            {/* Live results */}
            {results.length > 0 && (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="max-h-[200px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-secondary/60 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Account</th>
                        <th className="text-center px-2 py-1.5 text-muted-foreground font-medium">Status</th>
                        <th className="text-right px-2 py-1.5 text-muted-foreground font-medium">Project</th>
                        <th className="text-right px-2 py-1.5 text-muted-foreground font-medium">Cost Code</th>
                        <th className="text-right px-3 py-1.5 text-muted-foreground font-medium">Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r, idx) => (
                        <tr key={idx} className="border-t border-border/50">
                          <td className="px-3 py-1.5 text-foreground">{r.account_name}</td>
                          <td className="px-2 py-1.5 text-center">
                            {r.success ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mx-auto" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5 text-red-400 mx-auto" />
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right text-muted-foreground">{r.project}</td>
                          <td className="px-2 py-1.5 text-right text-muted-foreground">{r.cost_code}</td>
                          <td className="px-3 py-1.5 text-right text-muted-foreground">{r.reference}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Complete Phase */}
        {phase === 'complete' && (
          <div className="space-y-4 py-2">
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-lg font-semibold text-foreground">Bulk Backfill Complete</p>
              {cancelledRef.current && (
                <p className="text-yellow-400 text-sm mt-1">Operation was cancelled early</p>
              )}
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-secondary/50 rounded-lg p-3 border border-border text-center">
                <p className="text-xs text-muted-foreground">Accounts</p>
                <p className="text-xl font-bold text-foreground">{results.length}</p>
              </div>
              <div className="bg-green-900/20 rounded-lg p-3 border border-green-700/40 text-center">
                <p className="text-xs text-green-400">Succeeded</p>
                <p className="text-xl font-bold text-green-300">{successCount}</p>
              </div>
              {failCount > 0 && (
                <div className="bg-red-900/20 rounded-lg p-3 border border-red-700/40 text-center">
                  <p className="text-xs text-red-400">Failed</p>
                  <p className="text-xl font-bold text-red-300">{failCount}</p>
                </div>
              )}
              <div className="bg-yellow-900/20 rounded-lg p-3 border border-yellow-700/40 text-center">
                <p className="text-xs text-yellow-400">Fields Updated</p>
                <p className="text-xl font-bold text-yellow-300">{totalFieldsUpdated}</p>
              </div>
            </div>

            {/* Field breakdown */}
            <div className="bg-secondary/30 rounded-lg p-4 border border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3 font-medium">
                Fields Updated Breakdown
              </p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-lg font-bold text-foreground">{totalProjectUpdated}</p>
                  <p className="text-xs text-muted-foreground">Project</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{totalCostCodeUpdated}</p>
                  <p className="text-xs text-muted-foreground">Cost Code</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{totalReferenceUpdated}</p>
                  <p className="text-xs text-muted-foreground">Reference</p>
                </div>
              </div>
            </div>

            {/* Per-account results */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="max-h-[220px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/60 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 text-muted-foreground font-medium">Account</th>
                      <th className="text-center px-2 py-2 text-muted-foreground font-medium">Status</th>
                      <th className="text-right px-2 py-2 text-muted-foreground font-medium">Project</th>
                      <th className="text-right px-2 py-2 text-muted-foreground font-medium">Cost Code</th>
                      <th className="text-right px-3 py-2 text-muted-foreground font-medium">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, idx) => (
                      <tr key={idx} className="border-t border-border/50">
                        <td className="px-3 py-1.5">
                          <span className="text-foreground">{r.account_name}</span>
                          <span className="text-muted-foreground ml-1.5">({r.account_id})</span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {r.success ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mx-auto" />
                          ) : (
                            <span className="flex items-center justify-center gap-1 text-red-400">
                              <XCircle className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right text-muted-foreground">{r.project}</td>
                        <td className="px-2 py-1.5 text-right text-muted-foreground">{r.cost_code}</td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground">{r.reference}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Failed accounts detail */}
            {failCount > 0 && (
              <div className="bg-red-900/15 border border-red-700/40 rounded-lg p-3">
                <p className="text-xs text-red-400 font-medium mb-2">Failed Accounts:</p>
                {results
                  .filter((r) => !r.success)
                  .map((r, idx) => (
                    <p key={idx} className="text-xs text-red-300/80 ml-2">
                      {r.account_name} ({r.account_id}): {r.error}
                    </p>
                  ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {phase === 'preview' && eligibleAccounts.length > 0 && (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                className="border-border text-muted-foreground"
              >
                Cancel
              </Button>
              <Button className="golden-button" onClick={handleExecute}>
                <Play className="w-4 h-4 mr-1" />
                Run Bulk Backfill ({eligibleAccounts.length} accounts)
              </Button>
            </>
          )}

          {phase === 'preview' && eligibleAccounts.length === 0 && (
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-border text-muted-foreground"
            >
              Close
            </Button>
          )}

          {phase === 'running' && (
            <Button
              variant="outline"
              onClick={handleCancel}
              className="border-red-700 text-red-400 hover:bg-red-900/30"
            >
              <Square className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          )}

          {phase === 'complete' && (
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-border text-muted-foreground"
            >
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkBackfillDialog;
